import React, { useState, useMemo, useRef } from 'react';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
  arrow,
  FloatingArrow,
  type Placement,
} from '@floating-ui/react';

export interface TooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
  position?: Placement;
  className?: string;
  delay?: number;
  style?: 'light' | 'dark';
  trigger?: 'hover' | 'click' | 'none';
}

export const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  position = 'top',
  className = '',
  delay = 200,
  style = 'dark',
  trigger = 'hover',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const arrowRef = useRef(null);

  // Memoize middleware to prevent unnecessary re-runs and potential loops
  const middleware = useMemo(() => [
    offset(8),
    flip({
      fallbackAxisSideDirection: 'start',
    }),
    shift({ padding: 5 }),
    arrow({
      element: arrowRef,
    }),
  ], [arrowRef]);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: position,
    whileElementsMounted: autoUpdate,
    middleware,
  });

  const hover = useHover(context, {
    delay: { open: delay, close: 0 },
    enabled: trigger === 'hover',
  });
  const focus = useFocus(context, {
    enabled: trigger === 'hover',
  });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  // Memoize interactions array
  const interactions = useMemo(() => [hover, focus, dismiss, role], [hover, focus, dismiss, role]);
  
  const { getReferenceProps, getFloatingProps } = useInteractions(interactions);

  const isDarkMode = style === 'dark';

  return (
    <>
      <div
        ref={refs.setReference}
        {...getReferenceProps()}
        className="inline-block"
      >
        {children}
      </div>
      {isOpen && trigger !== 'none' && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className={`z-[9999] pointer-events-none select-none whitespace-nowrap rounded-lg px-3 py-2 text-sm shadow-xl transition-opacity duration-150 ${
              isDarkMode
                ? 'bg-bg-surface text-fg-primary border border-border-default'
                : 'bg-white text-gray-900 border border-gray-200'
            } ${className}`}
          >
            {content}
            <FloatingArrow
              ref={arrowRef}
              context={context}
              fill={isDarkMode ? 'var(--ep-background-surface)' : 'white'}
              stroke={isDarkMode ? 'var(--ep-border-default)' : '#e5e7eb'}
              strokeWidth={1}
            />
          </div>
        </FloatingPortal>
      )}
    </>
  );
};
